import { I18n } from "@assets/strings";
import React from "react";
import styled from "styled-components";
import { useNotificationsSettings } from "@domain/notification/hooks";
import {
	allNotificationSettingsCategories,
	NotificationCategory,
	NotificationMode,
} from "@domain/notification/notification";
import { Primary } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { Toggle } from "@ui/components/inputs/toggle";
import { Row, Stack } from "@ui/components/layout";

interface MobileNotificationSettingsProps extends React.HTMLAttributes<HTMLDivElement> {
	onSettingsChange: (item: NotificationCategory, mode: NotificationMode, enabled: boolean) => Promise<void>;
}

export const MobileNotificationSettings: React.FC<MobileNotificationSettingsProps> = ({
	onSettingsChange,
	...htmlProps
}) => {
	const { result: settings } = useNotificationsSettings();

	return (
		<Container {...htmlProps}>
			{allNotificationSettingsCategories.map((item) => (
				<Stack key={item} gap={27}>
					<div>
						<Primary>
							<b>{I18n.t(`setting.notifications.content.${item.toLowerCase()}.title`)}</b>
						</Primary>
						<Description>{I18n.t(`setting.notifications.content.${item.toLowerCase()}.description`)}</Description>
					</div>
					<Row gap={40} align="center">
						{(["EMAIL", "INAPP"] as const).map((type) => (
							<Row key={type} gap={6}>
								<Toggle
									disabled={item === "SYSTEM"}
									toggled={!!settings.find(({ category, mode }) => category === item && mode === type)}
									onChange={(enabled) => onSettingsChange(item, type, enabled)}
								/>
								<Type>
									{I18n.t(`settings.notifications.header.${type === "EMAIL" ? "email" : "app_notification"}`)}
								</Type>
							</Row>
						))}
					</Row>
				</Stack>
			))}
		</Container>
	);
};

const Container = styled(RoundedBlock)`
	> * {
		padding: 22px 18px 32px;
		&:not(:last-child) {
			border-bottom: 1px solid ${({ theme }) => theme.colors.lightergray};
		}
	}
`;

const Type = styled.div`
	${({ theme }) => theme.textStyles.tertiary};
	color: ${({ theme }) => theme.colors.textSecondary};
`;

const Description = styled.div`
	color: ${({ theme }) => theme.colors.textTertiary};
	font-size: 0.8125rem;
	margin-top: 5px;
`;
